<template>
  <div class="wallet-topup-selection">
    <!-- Main "buy credits" card -->
    <div class="wallet-topup-selection__card">
      <header class="wallet-topup-selection__card-head">
        <div class="wallet-topup-selection__card-icon">
          <i class="fa-regular fa-coins" />
        </div>
        <div>
          <h3 class="wallet-topup-selection__card-title">
            {{ t('billing.buyCredits.cardTitle') }}
          </h3>
          <p class="wallet-topup-selection__card-subtitle">
            {{ t('billing.buyCredits.cardSubtitle') }}
          </p>
        </div>
      </header>

      <div class="wallet-topup-selection__card-body">
        <!-- Amount stepper (shared) -->
        <AmountStepper
          :model-value="amount"
          :steps="AMOUNT_STEPS"
          :label="t('billing.buyCredits.amountLabel')"
          :suffix="currencySymbol"
          :max="100000"
          :increment-label="t('billing.addCredits.increment')"
          :decrement-label="t('billing.addCredits.decrement')"
          @update:model-value="$emit('update:amount', $event)"
        />

        <!-- Estimation panel (one-shoot rate only) -->
        <div
          v-if="selectedCountryData && amount > 0"
          class="wallet-topup-selection__estim"
        >
          <div class="wallet-topup-selection__estim-head">
            <div class="wallet-topup-selection__estim-eyebrow">
              {{ t('billing.buyCredits.estimationFor', { country: countryLabelOf(country) }) }}
            </div>
            <Dropdown
              :model-value="country"
              :options="countryOptions"
              option-label="label"
              option-value="value"
              filter
              :filter-placeholder="t('billing.buyCredits.searchCountry')"
              :auto-filter-focus="true"
              :reset-filter-on-hide="true"
              class="wallet-topup-selection__country"
              :disabled="disabled"
              @update:model-value="$emit('update:country', $event)"
            >
              <template #value="slotProps">
                <span v-if="slotProps.value">
                  {{ flagEmoji(slotProps.value) }} {{ countryLabelOf(slotProps.value) }}
                </span>
              </template>
              <template #option="slotProps">
                <span>
                  {{ flagEmoji(slotProps.option.value) }} {{ slotProps.option.label }}
                </span>
              </template>
            </Dropdown>
          </div>

          <div class="wallet-topup-selection__estim-quantity">
            <div class="wallet-topup-selection__estim-icon">
              <i class="fa-regular fa-message-sms" />
            </div>
            <div class="wallet-topup-selection__estim-block">
              <div class="wallet-topup-selection__estim-row">
                <span class="wallet-topup-selection__estim-num">
                  ~ {{ formatNumber(estimatedSms) }}
                </span>
                <span class="wallet-topup-selection__estim-pill">SMS</span>
              </div>
              <div class="wallet-topup-selection__estim-caption">
                {{ t('billing.buyCredits.smsPerMonthCaption') }}
              </div>
            </div>
          </div>

          <div class="wallet-topup-selection__estim-rate">
            <div class="wallet-topup-selection__estim-rate-label">
              {{ t('billing.buyCredits.unitRateLabel') }}
            </div>
            <div class="wallet-topup-selection__estim-rate-value">
              {{ formatUnitPrice(selectedCountryData.unitPriceOneShoot) }}
              <span class="wallet-topup-selection__estim-rate-unit">/ SMS</span>
            </div>
          </div>
        </div>

        <!-- Info note -->
        <div class="wallet-topup-selection__note">
          <i class="fa-regular fa-circle-info" />
          <span>{{ t('billing.buyCredits.note') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, toRef, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import AmountStepper from '@/components/billing/dialogs/AmountStepper.vue';
import { useSmsCountries } from '@/composables/billing/useSmsCountries';
import { billingCurrencySymbol } from '@/composables/billing/formatAmount';

const AMOUNT_STEPS = [
  10, 25, 50, 75, 100, 125, 150, 200, 500, 1000, 2000, 3000, 5000, 10000,
  20000, 50000, 100000,
];

export default defineComponent({
  name: 'WalletTopUpSelection',

  components: { Dropdown, AmountStepper },

  props: {
    amount: {
      type: Number,
      required: true,
    },

    country: {
      type: String,
      required: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    /** Shop billing currency — drives the stepper suffix + unit-rate symbol. */
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },
  },

  emits: ['update:amount', 'update:country'],

  setup(props) {
    const { t } = useI18n();
    const sms = useSmsCountries('oneShoot');
    const currencySymbol = computed(() => billingCurrencySymbol(props.currency));

    // Keep our `country` prop in sync with the composable's selection so the
    // estimation (selectedCountryData) reflects the parent-controlled value.
    watch(
      () => props.country,
      (v) => { sms.selectedCountry.value = v; },
      { immediate: true },
    );

    const amountRef = toRef(props, 'amount');
    const estimatedSms = computed(() => sms.estimatedSmsCount(amountRef.value));

    const formatUnitPrice = (n: number) => {
      const value = n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
      return props.currency === 'USD'
        ? `${currencySymbol.value}${value}`
        : `${value} ${currencySymbol.value}`;
    };
    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    return {
      t,
      AMOUNT_STEPS,
      selectedCountryData: sms.selectedCountryData,
      countryOptions: sms.countryOptions,
      countryLabelOf: sms.countryLabelOf,
      flagEmoji: sms.flagEmoji,
      estimatedSms,
      currencySymbol,
      formatUnitPrice,
      formatNumber,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.wallet-topup-selection {
  display: flex;
  flex-direction: column;
  gap: 16px;

  // ─── Outer card ─────────────────────────────────────
  &__card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 20px 22px;
    display: flex;
    flex-direction: column;
  }

  &__card-head {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 14px;
    padding-bottom: 18px;
    border-bottom: 1px dashed #e2e8f0;
    text-align: left;
  }

  &__card-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(148, 200, 64, 0.16);
    color: #629d03;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  &__card-title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.2;
  }

  &__card-subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.4;
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding-top: 20px;
  }

  // ─── Estimation panel ───────────────────────────────
  &__estim {
    background: linear-gradient(135deg, #fafff5 0%, #ffffff 100%);
    border: 1px solid #ecf3e3;
    border-radius: 14px;
    padding: 18px 20px;
  }

  &__estim-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  &__estim-eyebrow {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #629d03;
  }

  :deep(.wallet-topup-selection__country) {
    height: 34px;
    min-width: 160px;
    border-radius: 100px;
    border: 1px solid #d6e3c1;
    background: #fff;
    align-items: center;

    .p-dropdown-label {
      padding: 0 12px;
      display: flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #0f172a;
    }

    .p-dropdown-trigger {
      width: 32px;
      color: #629d03;
    }

    &:hover {
      border-color: $brand-color-primary;
    }
  }

  &__estim-quantity {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 16px;
  }

  &__estim-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: $brand-color-primary;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.375rem;
    flex-shrink: 0;
    box-shadow: 0 4px 12px -4px rgba(148, 200, 64, 0.55);
  }

  &__estim-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__estim-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  &__estim-num {
    font-size: 2.25rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  &__estim-pill {
    background: #0f172a;
    color: #fff;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 0.6875rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  &__estim-caption {
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.4;
  }

  &__estim-rate {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 14px;
    border-top: 1px dashed #d6e3c1;
  }

  &__estim-rate-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    font-weight: 700;
  }

  &__estim-rate-value {
    font-size: 1.125rem;
    font-weight: 800;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }

  &__estim-rate-unit {
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 500;
    margin-left: 2px;
  }

  // ─── Info note ──────────────────────────────────────
  &__note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #f6faff;
    border-radius: 10px;
    padding: 12px 14px;
    color: #475569;
    font-size: 0.8125rem;
    line-height: 1.5;

    i {
      color: #1d4ed8;
      margin-top: 2px;
      flex-shrink: 0;
    }
  }
}
</style>
